// src/utils/ocrProcessor.js
// ─────────────────────────────────────────────────────────────────────────────
// Image and PDF pre-processor.
// No Tesseract — Claude handles all OCR.
// This module only does compression and routing.
//
// Image flow:
//   Compress to max 2000×2700px → send to Claude as image/jpeg
//
// PDF flow:
//   pdf.js renders each page (up to 3) at scale 2.0 → compress to JPEG
//   → send to Claude as multiple image blocks
//   (If pdf.js fails, falls back to sending raw PDF as a document)
//
// Returns OCRRoute consumed by extractTextFromFile in WritingCoach.jsx.
// ─────────────────────────────────────────────────────────────────────────────

// ── Constants ─────────────────────────────────────────────────────────────────
export const PDF_PAGE_CAP = 3;

const IMAGE_MAX_WIDTH  = 2000;
const IMAGE_MAX_HEIGHT = 2700;
const PDF_RENDER_SCALE = 1.5;   // ~918×1188px for a letter-size page — sufficient for typed student work
const JPEG_QUALITY     = 0.92;

// pdf.js CDN — loaded on demand, cached by browser after first use.
const PDFJS_VERSION = "3.11.174";
const PDFJS_CDN     = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;

// ── Image compression via canvas ──────────────────────────────────────────────
function compressImageToBlob(file, maxW, maxH) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width: w, height: h } = img;
      // Only scale down — never upscale
      if (w > maxW || h > maxH) {
        const ratio = Math.min(maxW / w, maxH / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width  = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error("Compression failed")),
        "image/jpeg",
        JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
    img.src = url;
  });
}

// ── Canvas → base64 JPEG ──────────────────────────────────────────────────────
function canvasToBase64(canvas) {
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY).split(",")[1];
}

// ── Blob/File → base64 ────────────────────────────────────────────────────────
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("Base64 conversion failed"));
    reader.readAsDataURL(blob);
  });
}

// ── Load pdf.js from CDN (idempotent) ─────────────────────────────────────────
function loadPdfJs() {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const script    = document.createElement("script");
    script.src      = `${PDFJS_CDN}/pdf.min.js`;
    script.onload   = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    script.onerror  = () => reject(new Error("Failed to load pdf.js"));
    document.head.appendChild(script);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Main export ───────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
//
// Returns:
//   {
//     method:    'claude_image' | 'claude_pdf_pages' | 'claude_pdf_raw'
//     pages:     string[]   base64 JPEG strings (images + rendered PDF pages)
//     fileBase64: string | null  (raw PDF base64, for claude_pdf_raw fallback)
//     mediaType: 'image/jpeg' | 'application/pdf'
//     truncated: boolean
//     totalPages: number
//     extraTokens: 0  (always 0 — all OCR covered by base eval token)
//   }

export async function processOCR(file, mediaType, fileBase64, onProgress) {
  // ── PDF path ──────────────────────────────────────────────────────────────
  if (mediaType === "application/pdf") {
    if (onProgress) onProgress("Loading PDF…");

    let pdfjsLib;
    try {
      pdfjsLib = await loadPdfJs();
    } catch {
      // pdf.js CDN unavailable — fall back to sending raw PDF to Claude
      return {
        method: "claude_pdf_raw", pages: null,
        fileBase64, mediaType: "application/pdf",
        truncated: false, totalPages: 1, extraTokens: 0,
      };
    }

    // Decode base64 → Uint8Array
    let pdf;
    try {
      const binary = atob(fileBase64);
      const bytes  = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    } catch {
      // Corrupt or password-protected — fall back to raw PDF
      return {
        method: "claude_pdf_raw", pages: null,
        fileBase64, mediaType: "application/pdf",
        truncated: false, totalPages: 1, extraTokens: 0,
      };
    }

    const totalPages     = pdf.numPages;
    const pagesToProcess = Math.min(totalPages, PDF_PAGE_CAP);
    const truncated      = totalPages > PDF_PAGE_CAP;

    const pageBase64s = [];
    for (let p = 1; p <= pagesToProcess; p++) {
      if (onProgress) onProgress(`Reading page ${p} of ${pagesToProcess}…`);
      const page     = await pdf.getPage(p);
      const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
      const canvas   = document.createElement("canvas");
      canvas.width   = viewport.width;
      canvas.height  = viewport.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      pageBase64s.push(canvasToBase64(canvas));
    }

    return {
      method: "claude_pdf_pages", pages: pageBase64s,
      fileBase64: null, mediaType: "image/jpeg",
      truncated, totalPages, extraTokens: 0,
    };
  }

  // ── Image path ────────────────────────────────────────────────────────────
  if (onProgress) onProgress("Preparing image…");

  let base64;
  try {
    const compressed = await compressImageToBlob(file, IMAGE_MAX_WIDTH, IMAGE_MAX_HEIGHT);
    base64 = await blobToBase64(compressed);
  } catch {
    // Compression failed — use original
    base64 = fileBase64;
  }

  return {
    method: "claude_image", pages: [base64],
    fileBase64: null, mediaType: "image/jpeg",
    truncated: false, totalPages: 1, extraTokens: 0,
  };
}

// Human-readable label (kept for compatibility — always null now since
// we don't charge extra for OCR)
export function tierLabel() { return null; }
